import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const CW = 1024;
const CH = 1024;
const API_BASE = '/api/annotate';

function AnnotatePage() {
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);

  // MTurk params
  const hitId = searchParams.get('hitId') || '';
  const assignmentId = searchParams.get('assignmentId') || '';
  const workerId = searchParams.get('workerId') || '';
  const turkSubmitTo = searchParams.get('turkSubmitTo') || '';
  const sceneIdParam = searchParams.get('sceneId');
  const isMturk = !!hitId || !!assignmentId;
  const isPreview = assignmentId === 'ASSIGNMENT_ID_NOT_AVAILABLE';

  // Prolific params
  const prolificPid = searchParams.get('prolificPid') || searchParams.get('PROLIFIC_PID') || '';
  const studyId = searchParams.get('studyId') || searchParams.get('STUDY_ID') || '';
  const sessionId = searchParams.get('sessionId') || searchParams.get('SESSION_ID') || '';
  const isProlific = !!prolificPid;
  const isExternal = isMturk || isProlific;

  // State
  const [items, setItems] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [bgImage, setBgImage] = useState(null);
  const [objImages, setObjImages] = useState({});
  const [currentScene, setCurrentScene] = useState('hardwood_room_1');
  const [scenes, setScenes] = useState([]);
  const [objects, setObjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIdx, setSuggestionIdx] = useState(-1);
  const [currentEnv, setCurrentEnv] = useState('');
  const [status, setStatus] = useState('');
  const [suggestInfo, setSuggestInfo] = useState('');
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [originalSnapshot, setOriginalSnapshot] = useState(null); // data URL of original scene

  // Two-phase workflow for MTurk
  const [phase, setPhase] = useState(isExternal ? 'rate' : 'edit');
  const [originalRating, setOriginalRating] = useState(null);
  const [dragListIdx, setDragListIdx] = useState(null);

  // Batch mode: multiple scenes per HIT
  const batchSize = parseInt(searchParams.get('batchSize') || '0', 10);
  const batchStart = parseInt(searchParams.get('batchStart') || '0', 10);
  const isBatch = batchSize > 0;
  const [batchIdx, setBatchIdx] = useState(0); // current scene within batch
  const [batchCompleted, setBatchCompleted] = useState(0); // scenes rated/fixed so far

  // Load scene and object lists
  useEffect(() => {
    fetch(`${API_BASE}/scenes`).then(r => r.json()).then(d => setScenes(d.scenes || []));
    fetch(`${API_BASE}/objects`).then(r => r.json()).then(d => setObjects(d.objects || []));
  }, []);

  // Load background image
  const loadBg = useCallback((sceneName) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setBgImage(img);
    img.src = `${API_BASE}/scene/${sceneName}`;
    setCurrentScene(sceneName);
  }, []);

  useEffect(() => { loadBg(currentScene); }, []);

  // Load object image (cached)
  const loadObjImage = useCallback((label) => {
    return new Promise((resolve) => {
      if (objImages[label]) { resolve(objImages[label]); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setObjImages(prev => ({ ...prev, [label]: img }));
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = `${API_BASE}/object/${label}`;
    });
  }, [objImages]);

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CW, CH);
    if (bgImage) { ctx.drawImage(bgImage, 0, 0, CW, CH); }
    else { ctx.fillStyle = '#333'; ctx.fillRect(0, 0, CW, CH); }

    const sorted = [...items].map((item, i) => ({ ...item, _idx: i }))
      .sort((a, b) => (a.layer || 0) - (b.layer || 0));

    sorted.forEach(item => {
      const img = objImages[item.label];
      if (!img) return;
      const imgRatio = img.width / img.height;
      const h = CH * item.scale / 100;
      const w = h * imgRatio;
      const cx = CW * item.x / 100;
      const cy = CH * item.y / 100;
      ctx.drawImage(img, cx - w/2, cy - h/2, w, h);
      if (item._idx === selectedIdx) {
        ctx.strokeStyle = '#00ff00'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
        ctx.strokeRect(cx - w/2 - 2, cy - h/2 - 2, w + 4, h + 4);
        ctx.setLineDash([]);
      }
    });
  }, [items, bgImage, objImages, selectedIdx]);

  useEffect(() => { draw(); }, [draw]);

  // Hit test
  const hitTest = useCallback((mx, my) => {
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      const img = objImages[item.label];
      if (!img) continue;
      const h = CH * item.scale / 100;
      const w = h * (img.width / img.height);
      const cx = CW * item.x / 100;
      const cy = CH * item.y / 100;
      if (mx >= cx - w/2 && mx <= cx + w/2 && my >= cy - h/2 && my <= cy + h/2) return i;
    }
    return -1;
  }, [items, objImages]);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * CW / rect.width, y: (e.clientY - rect.top) * CH / rect.height };
  };

  const onMouseDown = (e) => {
    if (phase === 'rate') return; // no dragging in rate phase
    const { x, y } = getCanvasCoords(e);
    const idx = hitTest(x, y);
    setSelectedIdx(idx);
    if (idx >= 0) {
      setDragging(true);
      setDragOffset({ x: x - CW * items[idx].x / 100, y: y - CH * items[idx].y / 100 });
    }
  };

  const onMouseMove = (e) => {
    if (!dragging || selectedIdx < 0) return;
    const { x, y } = getCanvasCoords(e);
    setItems(prev => prev.map((item, i) =>
      i === selectedIdx ? { ...item, x: (x - dragOffset.x) / CW * 100, y: (y - dragOffset.y) / CH * 100 } : item
    ));
  };

  const onMouseUp = () => { setDragging(false); };

  // Add object
  const addObject = async (label) => {
    const img = await loadObjImage(label);
    if (!img) return;
    setItems(prev => [...prev, {
      type: 'bg_object', label, x: 50, y: 70, scale: 20,
      rotation: 0, aspect: 100, flipH: false, layer: prev.length,
    }]);
    setSelectedIdx(items.length);
  };

  const deleteSelected = () => {
    if (selectedIdx < 0) return;
    setItems(prev => prev.filter((_, i) => i !== selectedIdx));
    setSelectedIdx(-1);
  };

  const scaleSelected = (delta) => {
    if (selectedIdx < 0) return;
    setItems(prev => prev.map((item, i) =>
      i === selectedIdx ? { ...item, scale: Math.max(2, Math.min(90, item.scale + delta)) } : item
    ));
  };

  const moveLayer = (direction) => {
    if (selectedIdx < 0) return;
    setItems(prev => prev.map((item, i) =>
      i === selectedIdx ? { ...item, layer: Math.max(0, (item.layer || 0) + direction) } : item
    ));
  };

  // Load suggestions
  const loadSuggestions = () => {
    let url;
    if (isBatch) {
      const seed = searchParams.get('seed') || prolificPid || '';
      const categories = searchParams.get('categories') || '';
      url = `${API_BASE}/suggested_scenes?batchStart=${batchStart}&batchSize=${batchSize}&seed=${seed}${categories ? '&categories=' + encodeURIComponent(categories) : ''}`;
    } else if (sceneIdParam != null) {
      url = `${API_BASE}/suggested_scenes?sceneId=${sceneIdParam}`;
    } else {
      url = `${API_BASE}/suggested_scenes`;
    }
    setSuggestInfo('Loading...');
    fetch(url).then(r => r.json()).then(d => {
      const sceneList = d.scenes || [];
      setSuggestions(sceneList);
      setSuggestionIdx(-1);
      setBatchIdx(0);
      setBatchCompleted(0);
      if (sceneList.length === 0) { setSuggestInfo('No suggestions found'); }
      else { loadNextSuggestion(sceneList, 0); }
    });
  };

  const loadNextSuggestion = (sceneList, idx) => {
    const list = sceneList || suggestions;
    const nextIdx = idx !== undefined ? idx : suggestionIdx + 1;
    if (nextIdx >= list.length) { setSuggestInfo('All suggestions reviewed!'); return; }
    const scene = list[nextIdx];
    setSuggestionIdx(nextIdx);
    const envName = (scene.environment || 'scene').replace(/_/g, ' ');
    setCurrentEnv(envName);
    setSuggestInfo(`${envName} (${nextIdx + 1}/${list.length})`);
    loadBg(scene.scene || 'hardwood_room_1');
    setItems([]); setSelectedIdx(-1);
    setPhase(isExternal ? 'rate' : 'edit');
    setOriginalRating(null);
    setCnnRejected(false); setCnnOverride(false);
    setStatus(`Loading ${scene.items.length} objects...`);
    Promise.all(scene.items.map(item => loadObjImage(item.label))).then(() => {
      setItems(scene.items.map((item, i) => ({
        type: item.type || 'bg_object', label: item.label,
        x: item.x, y: item.y, scale: item.scale,
        rotation: item.rotation || 0, aspect: item.aspect || 100,
        flipH: item.flipH || false, layer: item.layer || i,
      })));
      setStatus(`${scene.items.length} objects loaded`);
    });
  };

  // Phase 1: Rate original scene
  const rateOriginal = (verdict) => {
    if (items.length === 0) { setStatus('Wait for objects to load...'); return; }
    setOriginalRating(verdict);
    // Save the original rating
    const data = {
      scene: currentScene, verdict,
      items: items.map(({ label, x, y, scale, rotation, aspect, flipH, layer, type }) =>
        ({ type: type || 'bg_object', label, x, y, scale, rotation: rotation || 0, aspect: aspect || 100, flipH: flipH || false, layer: layer || 0 })),
      workerId: workerId || prolificPid, hitId: hitId || studyId, assignmentId: assignmentId || sessionId, phase: 'original_rating',
    };
    fetch(`${API_BASE}/label`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });

    if (verdict === 'good') {
      // Good scene — no rearrangement needed
      if (isBatch) {
        // Batch mode: advance to next scene
        const nextBatchIdx = batchIdx + 1;
        setBatchCompleted(nextBatchIdx);
        if (nextBatchIdx >= suggestions.length) {
          finishBatch(nextBatchIdx);
          return;
        }
        setBatchIdx(nextBatchIdx);
        setStatus(`Rated GOOD (${nextBatchIdx}/${suggestions.length}). Loading next scene...`);
        loadNextSuggestion(suggestions, nextBatchIdx);
        return;
      }

      if (isMturk && turkSubmitTo && !isPreview) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${turkSubmitTo}/mturk/externalSubmit`;
        const fields = { assignmentId, verdict: 'good', original_rating: 'good',
          scene: currentScene, items: JSON.stringify(data.items) };
        Object.entries(fields).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = k; input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        setSubmitted(true);
      } else {
        setStatus('Rated as GOOD.');
      }
      return;
    }

    // Bad scene — capture snapshot and move to edit phase
    if (canvasRef.current) {
      setOriginalSnapshot(canvasRef.current.toDataURL('image/png'));
    }
    setPhase('edit');
    setShowTutorial(false); // hide tutorial after first rating
    setStatus(isBatch
      ? `Rated BAD (${batchCompleted + 1}/${suggestions.length}). Rearrange the objects, then submit.`
      : 'Rated as BAD. Now rearrange the objects to fix the scene, then submit.');
  };

  // Phase 2: Submit final arrangement — CNN soft-gates
  const [submitting, setSubmitting] = useState(false);
  const [cnnRejected, setCnnRejected] = useState(false);
  const [cnnOverride, setCnnOverride] = useState(false);

  const submitLabel = (verdict, forceOverride) => {
    const sceneData = {
      scene: currentScene,
      items: items.map(({ label, x, y, scale, rotation, aspect, flipH, layer, type }) =>
        ({ type: type || 'bg_object', label, x, y, scale, rotation: rotation || 0, aspect: aspect || 100, flipH: flipH || false, layer: layer || 0 })),
    };

    // For external workers: check with CNN first (soft pushback)
    if (isExternal && verdict === 'good' && !cnnOverride && !forceOverride) {
      setSubmitting(true);
      setStatus('Checking scene quality...');
      fetch(`${API_BASE}/classify_cnn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sceneData),
      }).then(r => r.json()).then(clf => {
        setSubmitting(false);
        if (clf.prediction === 'bad') {
          setCnnRejected(true);
          setStatus('Our quality checker thinks this scene doesn\'t look realistic yet — objects may be floating, overlapping, or wrongly sized. Please try adjusting, or click "Submit Anyway" if you\'re sure it looks good.');
          return;
        }
        setCnnRejected(false);
        doSubmit(verdict, sceneData);
      }).catch(() => {
        setSubmitting(false);
        doSubmit(verdict, sceneData);
      });
      return;
    }
    setCnnOverride(false);
    setCnnRejected(false);

    doSubmit(verdict, sceneData);
  };

  const doSubmit = (verdict, sceneData) => {
    const data = {
      ...sceneData,
      verdict,
      workerId: workerId || prolificPid, hitId: hitId || studyId, assignmentId: assignmentId || sessionId,
      original_rating: originalRating, phase: 'final', cnn_override: cnnOverride,
    };
    fetch(`${API_BASE}/label`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
      .then(r => r.json()).then(d => {
        if (d.success) {
          if (isBatch) {
            // One edit per scene — auto advance
            const nextBatchIdx = batchIdx + 1;
            setBatchCompleted(nextBatchIdx);
            if (nextBatchIdx >= suggestions.length) {
              finishBatch(nextBatchIdx);
              return;
            }
            setBatchIdx(nextBatchIdx);
            setStatus('Submitted! Loading next scene...');
            loadNextSuggestion(suggestions, nextBatchIdx);
            return;
          }

          setStatus(`Labeled as ${verdict.toUpperCase()}`);
          if (isMturk && turkSubmitTo && !isPreview) {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = `${turkSubmitTo}/mturk/externalSubmit`;
            const fields = { assignmentId, verdict, original_rating: originalRating || '',
              scene: currentScene, items: JSON.stringify(data.items) };
            Object.entries(fields).forEach(([k, v]) => {
              const input = document.createElement('input');
              input.type = 'hidden'; input.name = k; input.value = v;
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            setSubmitted(true);
          }
        }
      });
  };

  // Finish batch — submit to MTurk or redirect to Prolific
  const finishBatch = (completed) => {
    setStatus(`Batch complete! ${completed} scenes reviewed. Submitting...`);
    if (isMturk && turkSubmitTo && !isPreview) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${turkSubmitTo}/mturk/externalSubmit`;
      const fields = { assignmentId, batchCompleted: String(completed),
        batchSize: String(batchSize), batchStart: String(batchStart) };
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = k; input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      setSubmitted(true);
    } else if (isProlific) {
      // Redirect to Prolific completion URL
      setSubmitted(true);
      window.location.href = `https://app.prolific.com/submissions/complete?cc=VIGR_COMPLETE`;
    } else {
      setSubmitted(true);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if (phase === 'rate') return;
      if (e.key === 'Delete' || e.key === 'Backspace') { deleteSelected(); e.preventDefault(); }
      if (e.key === '=' || e.key === '+') { scaleSelected(2); e.preventDefault(); }
      if (e.key === '-') { scaleSelected(-2); e.preventDefault(); }
      if (e.key === 'f' || e.key === 'F') { moveLayer(1); e.preventDefault(); }
      if (e.key === 'b' || e.key === 'B') { moveLayer(-1); e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Auto-load for MTurk/Prolific
  useEffect(() => {
    if (isExternal && (sceneIdParam != null || isBatch)) { loadSuggestions(); }
  }, [isExternal, sceneIdParam, isBatch]);

  const filteredObjects = objects.filter(o =>
    o.toLowerCase().replace(/_/g, ' ').includes(searchQuery.toLowerCase())
  );

  if (submitted) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Thank you!</h2>
          <p>Your response has been submitted.</p>
        </div>
      </div>
    );
  }

  if (isPreview) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
        <div style={{ textAlign: 'center', maxWidth: 500, padding: '2rem' }}>
          <h2>Scene Composition Task</h2>
          <p style={{ marginBottom: '1rem' }}>In this task you will:</p>
          <ol style={{ textAlign: 'left', lineHeight: 2.2 }}>
            <li>Rate whether objects in the scene are in realistic positions</li>
            <li>Rearrange the objects so they look like they belong in a real room</li>
            <li>Submit your improved arrangement</li>
          </ol>
          <p style={{ color: '#888', marginTop: '1.5rem' }}>Accept the HIT to begin.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'system-ui, sans-serif', background: '#f5f5f5' }}>
      {/* Sidebar */}
      <div style={{ width: 300, background: 'white', borderRight: '1px solid #e0e0e0', padding: '1rem', overflowY: 'auto', flexShrink: 0 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          {isExternal ? 'Scene Composition Task' : 'Annotate Scene'}
        </h2>
        {isBatch && (
          <div style={{ marginBottom: '0.75rem', padding: '0.4rem 0.6rem', background: '#e3f2fd', borderRadius: 6, fontSize: '0.8rem' }}>
            Scene {batchIdx + 1} of {suggestions.length}
            <div style={{ marginTop: 4, height: 4, background: '#ccc', borderRadius: 2 }}>
              <div style={{ height: '100%', background: '#2196f3', borderRadius: 2,
                width: `${(batchIdx / Math.max(suggestions.length, 1)) * 100}%`, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {/* Tutorial (collapsible) */}
        {isExternal && showTutorial && (
          <div style={{ marginBottom: '0.75rem', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <div onClick={() => setShowTutorial(!showTutorial)}
              style={{ padding: '0.5rem 0.75rem', background: '#f5f5f5', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
              <span>How this works</span>
              <span>▾</span>
            </div>
            <div style={{ padding: '0.75rem', fontSize: '0.75rem', lineHeight: 1.5, color: '#444' }}>
              {[
                { title: '1. Rating', desc: 'Mark Good if realistic, Bad if not', before: 'tut_rate_bad', after: 'tut_rate_good' },
                { title: '2. Drag to move', desc: 'Click and drag objects to the floor', before: 'tut_drag_before', after: 'tut_drag_after' },
                { title: '3. Resize', desc: 'Use slider or +/- keys', before: 'tut_resize_before', after: 'tut_resize_after' },
                { title: '4. Reorder layers', desc: 'Press F (front) / B (back)', before: 'tut_zorder_before', after: 'tut_zorder_after' },
                { title: '5. Delete', desc: 'Select and press Delete key', before: 'tut_delete_before', after: 'tut_delete_after' },
                { title: '6. Add objects', desc: 'Search and click from the list', before: 'tut_insert_before', after: 'tut_insert_after' },
              ].map((step, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 2 }}>{step.title}</div>
                  <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: 3 }}>{step.desc}</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <img src={'/api/annotate/scene/' + step.before} alt="" style={{ width: '48%', borderRadius: 3, border: '1px solid #e0e0e0' }} />
                    <span style={{ fontSize: '1rem' }}>→</span>
                    <img src={'/api/annotate/scene/' + step.after} alt="" style={{ width: '48%', borderRadius: 3, border: '1px solid #e0e0e0' }} />
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#e3f2fd', borderRadius: 4, color: '#1565c0', fontWeight: 500 }}>
                One submission per scene — make it count!
              </div>
            </div>
          </div>
        )}

        {/* Phase 1: Rate original */}
        {phase === 'rate' && (
          <div style={{ padding: '0.75rem', background: '#fff8e1', borderRadius: 8, marginBottom: '1rem', border: '1px solid #ffe082' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Step 1: Rate this layout</div>
            {currentEnv && (
              <div style={{ fontSize: '0.8rem', color: '#1565c0', marginBottom: '0.5rem', padding: '4px 8px', background: '#e3f2fd', borderRadius: 4 }}>
                This scene is meant to be a <strong>{currentEnv}</strong>. Keep objects that belong in this type of room.
              </div>
            )}
            <p style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.5rem', lineHeight: 1.6 }}>
              Look at where the objects are placed. Could this be a photo of a real room?
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <img src="/api/annotate/scene/example_bad" alt="Bad example"
                  style={{ width: '100%', borderRadius: 4, border: '2px solid #f44336' }} />
                <div style={{ fontSize: '0.7rem', color: '#f44336', fontWeight: 600, marginTop: 2 }}>BAD — floating, too large</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <img src="/api/annotate/scene/example_good" alt="Good example"
                  style={{ width: '100%', borderRadius: 4, border: '2px solid #4caf50' }} />
                <div style={{ fontSize: '0.7rem', color: '#4caf50', fontWeight: 600, marginTop: 2 }}>GOOD — on the floor, realistic</div>
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem', lineHeight: 1.8 }}>
              <div>Mark <strong style={{ color: '#4caf50' }}>Good</strong> if all objects are resting on the floor at realistic sizes. Objects can be partially behind other objects — that's fine.</div>
              <div style={{ marginTop: 4 }}>Mark <strong style={{ color: '#f44336' }}>Bad</strong> if anything looks unrealistic:</div>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                <li>Objects floating in the air instead of on the floor</li>
                <li>Objects way too large or too small for the room</li>
                <li>Objects overlapping in an impossible way</li>
              </ul>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => rateOriginal('good')} style={{ ...btnLabel, background: '#4caf50', color: 'white' }}>Good</button>
              <button onClick={() => rateOriginal('bad')} style={{ ...btnLabel, background: '#f44336', color: 'white' }}>Bad</button>
            </div>
          </div>
        )}

        {/* Phase 2: Edit and submit */}
        {phase === 'edit' && (
          <>
            {isExternal && originalRating && (
              <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                You rated the original as <strong style={{ color: originalRating === 'good' ? '#4caf50' : '#f44336' }}>{originalRating.toUpperCase()}</strong>.
              </div>
            )}

            {currentEnv && (
              <div style={{ fontSize: '0.8rem', color: '#1565c0', marginBottom: '0.5rem', padding: '4px 8px', background: '#e3f2fd', borderRadius: 4 }}>
                This is a <strong>{currentEnv}</strong>. Keep objects that belong in this type of room.
              </div>
            )}

            {/* Instructions */}
            <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: 8, fontSize: '0.8rem', lineHeight: 1.8, color: '#444' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{isExternal ? 'Step 2: Arrange objects' : 'How to edit:'}</div>
              <div>Drag objects to realistic positions on the floor</div>
              <div>Use the slider or <kbd style={kbd}>+</kbd>/<kbd style={kbd}>-</kbd> to resize</div>
              <div>Press <kbd style={kbd}>Delete</kbd> to remove objects that don't fit</div>
              <div>Press <kbd style={kbd}>F</kbd>/<kbd style={kbd}>B</kbd> to change front/back order</div>
            </div>

            {/* Selected object controls — fixed height to prevent layout shift */}
            <div style={{ marginBottom: '0.75rem', padding: '0.5rem', background: selectedIdx >= 0 && items[selectedIdx] ? '#f0f7ff' : '#f9f9f9', borderRadius: 6, minHeight: 80 }}>
              {selectedIdx >= 0 && items[selectedIdx] ? (
                <>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
                    {items[selectedIdx].label.replace(/_/g, ' ')}
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: 2 }}>Scale: {items[selectedIdx].scale.toFixed(0)}%</div>
                    <input type="range" min="2" max="90" value={items[selectedIdx].scale}
                      onChange={e => setItems(prev => prev.map((item, i) =>
                        i === selectedIdx ? { ...item, scale: parseFloat(e.target.value) } : item
                      ))}
                      style={{ width: '100%' }} />
                  </div>
                  <button onClick={deleteSelected} style={{ ...btnSmall, color: '#c33', borderColor: '#c33' }}>Delete</button>
                </>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#aaa', paddingTop: 8 }}>Click an object to select it</div>
              )}
            </div>

            {/* Draggable layer list */}
            {items.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: 4 }}>Layer order (drag to reorder, top = front):</div>
                {[...items]
                  .map((item, i) => ({ ...item, _idx: i }))
                  .sort((a, b) => (b.layer || 0) - (a.layer || 0))
                  .map((item) => (
                    <div key={item._idx}
                      draggable
                      onDragStart={() => setDragListIdx(item._idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragListIdx === null || dragListIdx === item._idx) return;
                        setItems(prev => {
                          const updated = [...prev];
                          const dragLayer = updated[dragListIdx].layer || 0;
                          const dropLayer = updated[item._idx].layer || 0;
                          updated[dragListIdx] = { ...updated[dragListIdx], layer: dropLayer };
                          updated[item._idx] = { ...updated[item._idx], layer: dragLayer };
                          return updated;
                        });
                        setDragListIdx(null);
                      }}
                      onClick={() => setSelectedIdx(item._idx)}
                      style={{
                        padding: '4px 8px', marginBottom: 2, borderRadius: 4, fontSize: '0.8rem',
                        cursor: 'grab', userSelect: 'none',
                        background: item._idx === selectedIdx ? '#d0e4ff' : '#f9f9f9',
                        border: item._idx === selectedIdx ? '1px solid #4a90d9' : '1px solid #e0e0e0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                      <span>⠿ {item.label.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: '0.7rem', color: '#999' }}>{item.layer || 0}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* Scene selector (non-MTurk) */}
            {!isExternal && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>Background</label>
                <select value={currentScene} onChange={e => loadBg(e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', fontSize: '0.85rem', borderRadius: 4, border: '1px solid #ccc' }}>
                  {scenes.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            )}

            {/* Object search + add */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#666' }}>Add Object</label>
              <input type="text" placeholder="Search objects..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '0.4rem', fontSize: '0.85rem', borderRadius: 4, border: '1px solid #ccc', marginBottom: 4, boxSizing: 'border-box' }} />
              <select size={isExternal ? 4 : 6} style={{ width: '100%', fontSize: '0.8rem', borderRadius: 4, border: '1px solid #ccc' }}
                onChange={e => { addObject(e.target.value); e.target.selectedIndex = -1; }}>
                {filteredObjects.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            {/* Load suggestions (non-MTurk) */}
            {!isExternal && (
              <div style={{ marginBottom: '1rem' }}>
                <button onClick={loadSuggestions} style={btnAction}>Load Suggestions</button>
                {suggestions.length > 0 && (
                  <button onClick={() => loadNextSuggestion()} style={{ ...btnAction, marginTop: 4 }}>Next Suggestion</button>
                )}
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>{suggestInfo}</div>
              </div>
            )}

            {/* Submit buttons */}
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                {isExternal ? 'Step 3: Submit your arrangement' : 'Label this scene'}
              </div>
              {isExternal && (
                <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                  Submit when all objects are in realistic positions.
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setCnnOverride(false); submitLabel('good'); }} disabled={submitting}
                  style={{ ...btnLabel, background: submitting ? '#aaa' : '#4caf50', color: 'white', cursor: submitting ? 'wait' : 'pointer' }}>
                  {submitting ? 'Checking...' : 'Submit as Good'}
                </button>
                {cnnRejected && (
                  <button onClick={() => { setCnnOverride(true); submitLabel('good', true); }}
                    style={{ ...btnLabel, background: '#ff9800', color: 'white', fontSize: '0.8rem' }}>
                    Submit Anyway
                  </button>
                )}
              </div>
            </div>

            <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem' }}>{status}</div>
          </>
        )}
      </div>

      {/* Canvas area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', maxWidth: '100%' }}>
          {/* Before/after: show original snapshot during edit phase */}
          {phase === 'edit' && originalSnapshot && (
            <div style={{ flexShrink: 0, textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#c33', fontWeight: 600, marginBottom: 4 }}>Original (Bad)</div>
              <img src={originalSnapshot} alt="Original"
                style={{ width: 250, height: 250, borderRadius: 4, border: '2px solid #f44336', objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ textAlign: 'center', flex: 1 }}>
            {phase === 'edit' && originalSnapshot && (
              <div style={{ fontSize: '0.75rem', color: '#4caf50', fontWeight: 600, marginBottom: 4 }}>Your Edit</div>
            )}
            <canvas ref={canvasRef} width={CW} height={CH}
              style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 4rem)', border: phase === 'edit' && originalSnapshot ? '2px solid #4caf50' : '1px solid #ccc',
                       borderRadius: 4, cursor: phase === 'rate' ? 'default' : dragging ? 'grabbing' : 'default' }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const btnSmall = {
  padding: '2px 8px', fontSize: '0.8rem', borderRadius: 4,
  border: '1px solid #ccc', background: 'white', cursor: 'pointer',
};
const btnAction = {
  width: '100%', padding: '0.5rem', fontSize: '0.85rem',
  borderRadius: 6, border: '1px solid #ccc', background: 'white',
  cursor: 'pointer', display: 'block',
};
const btnLabel = {
  flex: 1, padding: '0.7rem', fontSize: '1rem', fontWeight: 600,
  borderRadius: 8, border: 'none', cursor: 'pointer',
};
const kbd = {
  display: 'inline-block', padding: '1px 5px', fontSize: '0.75rem',
  border: '1px solid #ccc', borderRadius: 3, background: '#eee',
  fontFamily: 'monospace',
};

export default AnnotatePage;
